import React from "react";
import { motion } from "framer-motion";
import { Camera, Sparkles, ArrowRight, Phone } from "lucide-react";
import { BUSINESS } from "../../lib/constants";

const SAMPLE_JOBS = [
  {
    label: "Garage Cleanout",
    location: "Liverpool, NY",
    notes: "Tools, tires, scrap wood & years of buildup — hauled in 2 hrs.",
    tag: "Residential",
    beforeUrl:
      "https://customer-assets.emergentagent.com/job_haul-it-demo/artifacts/onga9akq_IMG_1789.jpeg",
    afterUrl:
      "https://customer-assets.emergentagent.com/job_haul-it-demo/artifacts/xogotzj3_IMG_1790.jpeg",
  },
  {
    label: "Basement Haul-Out",
    location: "Cicero, NY",
    notes: "Old furniture, boxes, appliances, carpet — full clear-out.",
    tag: "Residential",
    beforeUrl:
      "https://customer-assets.emergentagent.com/job_haul-it-demo/artifacts/1vpycc9w_IMG_1803.jpeg",
    afterUrl:
      "https://customer-assets.emergentagent.com/job_haul-it-demo/artifacts/f8po3uke_IMG_1804.jpeg",
  },
  {
    label: "Yard & Debris",
    location: "Camillus, NY",
    notes: "Branches, sod, fence sections & construction waste removed.",
    tag: "Yard Waste",
    beforeUrl: "/gallery/camillus_before.jpg",
    afterUrl: "/gallery/camillus_after.jpg",
  },
  {
    label: "Shed Demolition",
    location: "Baldwinsville, NY",
    notes: "Torn down, loaded, hauled — pad swept clean in an afternoon.",
    tag: "Light Demo",
    beforeUrl: "/gallery/baldwinsville_before.jpg",
    afterUrl: "/gallery/baldwinsville_after.jpg",
  },
];

const Placeholder = ({ side, label }) => (
  <div
    className={`relative aspect-[4/3] overflow-hidden border-2 ${
      side === "after" ? "border-gold bg-navy" : "border-navy/70 bg-navy-dark"
    }`}
  >
    {/* diagonal stripes pattern */}
    <div
      className="absolute inset-0 opacity-20"
      style={{
        backgroundImage:
          side === "before"
            ? "repeating-linear-gradient(45deg, #ffffff 0 2px, transparent 2px 18px)"
            : "repeating-linear-gradient(-45deg, #f5a623 0 2px, transparent 2px 22px)",
      }}
    />
    {/* grid overlay */}
    <div
      className="absolute inset-0 opacity-10"
      style={{
        backgroundImage:
          "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }}
    />
    {/* center content */}
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
      <Camera
        className={`h-10 w-10 mb-3 ${
          side === "after" ? "text-gold" : "text-white/70"
        }`}
        strokeWidth={1.5}
      />
      <p
        className={`font-display uppercase tracking-[0.28em] text-[11px] sm:text-xs ${
          side === "after" ? "text-gold" : "text-white/70"
        }`}
      >
        Photo Coming Soon
      </p>
      <p className="mt-2 font-body text-white/60 text-xs max-w-[240px] leading-relaxed">
        {label}
      </p>
    </div>
    <span className="absolute top-4 left-4 label-pill !bg-white/90">
      {side === "before" ? "Before" : "After"}
    </span>
  </div>
);

const RealPhoto = ({ side, url, caption }) => (
  <div
    className={`relative aspect-[4/3] overflow-hidden border-2 bg-navy ${
      side === "after" ? "border-gold" : "border-navy/70"
    }`}
  >
    <img
      src={url}
      alt={`${side} — ${caption}`}
      className="absolute inset-0 w-full h-full object-cover"
      loading="lazy"
    />
    <span
      className={`absolute top-4 left-4 label-pill ${
        side === "after"
          ? "!bg-gold !border-gold !text-navy"
          : "!bg-white/90"
      }`}
    >
      {side === "before" ? "Before" : "After"}
    </span>
  </div>
);

const JobCard = ({ job, index }) => {
  const hasRealPhotos = Boolean(job.beforeUrl && job.afterUrl);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      data-testid={`gallery-job-${index}`}
      className="border-2 border-navy bg-navy"
    >
      <div className="grid grid-cols-2">
        {hasRealPhotos ? (
          <>
            <RealPhoto
              side="before"
              url={job.beforeUrl}
              caption={`${job.label} — ${job.location}`}
            />
            <RealPhoto
              side="after"
              url={job.afterUrl}
              caption={`${job.label} — ${job.location}`}
            />
          </>
        ) : (
          <>
            <Placeholder
              side="before"
              label={`Before we arrived — ${job.notes}`}
            />
            <Placeholder
              side="after"
              label={`After our crew — clean, empty, swept.`}
            />
          </>
        )}
      </div>
      <div
        className={`px-5 py-4 flex items-center justify-between gap-3 bg-navy text-white border-t-2 ${
          hasRealPhotos ? "border-gold" : "border-gold"
        }`}
      >
        <div className="min-w-0">
          <p className="font-display uppercase tracking-wider text-sm truncate">
            {job.label} — {job.location}
          </p>
          <p className="text-white/60 text-[11px] mt-0.5 truncate">
            {job.notes}
          </p>
        </div>
        <span className="label-pill !bg-gold !border-gold !text-navy shrink-0">
          {job.tag}
        </span>
      </div>
    </motion.div>
  );
};

export const BeforeAfter = () => {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-navy text-white py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_20%_10%,#f5a623_0,transparent_40%),radial-gradient(circle_at_80%_80%,#f5a623_0,transparent_35%)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-3xl">
          <span className="label-pill !bg-gold !border-gold !text-navy">
            <Sparkles className="h-3 w-3" strokeWidth={2.5} />
            02 — Real jobs. Real results.
          </span>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.95]">
            We don't just talk —
            <br />
            <span className="text-gold">we haul.</span>
          </h2>
          <p className="mt-6 text-white/85 text-base sm:text-lg leading-relaxed max-w-2xl">
            Every photo below is a <strong className="text-gold">real B&amp;C
            job</strong> — same truck, same crew, same day we showed up.
            From hoarded basements to full shed demos, this is what
            "Syracuse Strong. Junk Gone." actually looks like.
          </p>
          <p className="mt-3 text-white/60 text-sm">
            Got a space that looks like one of these?{" "}
            <a
              href="#quote"
              className="text-gold underline underline-offset-4 decoration-gold/60 hover:decoration-gold"
            >
              Get a free quote
            </a>{" "}
            or{" "}
            <a
              href={BUSINESS.phoneHref}
              className="text-gold underline underline-offset-4 decoration-gold/60 hover:decoration-gold"
            >
              call {BUSINESS.phoneDisplay}
            </a>
            .
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {SAMPLE_JOBS.map((job, i) => (
            <JobCard key={job.label} job={job} index={i} />
          ))}
        </div>

        {/* Call-out below gallery */}
        <div className="mt-14 border-2 border-gold bg-navy-dark px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-display uppercase text-white text-xl sm:text-2xl leading-tight">
              Got a job like these? <span className="text-gold">We'll haul it.</span>
            </p>
            <p className="text-white/70 text-sm mt-1">
              Weekend slots open · Free quotes · Fully insured
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={BUSINESS.phoneHref}
              data-testid="gallery-call-btn"
              className="brutal-btn brutal-btn-gold inline-flex items-center gap-2 bg-gold text-navy px-5 py-3 font-display text-sm uppercase tracking-wider border-2 border-gold"
            >
              <Phone className="h-4 w-4" strokeWidth={3} />
              {BUSINESS.phoneDisplay}
            </a>
            <a
              href="#quote"
              className="inline-flex items-center gap-2 bg-transparent text-white px-5 py-3 font-display text-sm uppercase tracking-wider border-2 border-white hover:bg-white hover:text-navy transition-colors"
            >
              Free Quote
              <ArrowRight className="h-4 w-4" strokeWidth={3} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
